export const TRACKED_ORDER_STORAGE_KEY = 'vemmac_tracked_order';
export const TRACKED_ORDER_EVENT = 'vemmac:tracked-order-updated';

const ACTIVE_ORDER_STATES = new Set([
  'pending',
  'pending_verification',
  'verified',
  'processing',
]);

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const normalizeState = (value) => String(value || '').trim().toLowerCase();

const parseDate = (order) => {
  const value = order?.created_at || order?.createdAt || order?.requested_date || order?.requestedDate;
  const time = Date.parse(value || '');
  return Number.isNaN(time) ? 0 : time;
};

const notifyTrackedOrderChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TRACKED_ORDER_EVENT));
  }
};

export const getOrderId = (order) =>
  order?.id || order?.orderId || order?.order_id || order?.reference || '';

export const isActiveOrder = (order) => {
  if (!order || !getOrderId(order)) return false;

  const status = normalizeState(order.status);
  const paymentStatus = normalizeState(order.payment_status || order.paymentStatus);

  return ACTIVE_ORDER_STATES.has(status) || ACTIVE_ORDER_STATES.has(paymentStatus);
};

export const findActiveOrder = (orders = []) =>
  [...orders]
    .filter(isActiveOrder)
    .sort((a, b) => parseDate(b) - parseDate(a))[0] || null;

export const getOrderTrackingPath = (order) => {
  const id = getOrderId(order);
  return id ? `/order-status/${id}` : '';
};

export const readTrackedOrder = () => {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(TRACKED_ORDER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return getOrderId(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeTrackedOrder = (order, { notify = true } = {}) => {
  const id = getOrderId(order);
  if (!id || !canUseStorage()) return;

  const payload = {
    id,
    status: order.status || 'pending',
    payment_status: order.payment_status || order.paymentStatus || '',
    payment_method: order.payment_method || order.paymentMethod || '',
    created_at: order.created_at || order.createdAt || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(TRACKED_ORDER_STORAGE_KEY, JSON.stringify(payload));
    if (notify) notifyTrackedOrderChange();
  } catch {
    // Tracking visibility should not block checkout or navigation.
  }
};

export const clearTrackedOrder = ({ notify = true } = {}) => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.removeItem(TRACKED_ORDER_STORAGE_KEY);
    if (notify) notifyTrackedOrderChange();
  } catch {
    // Ignore storage failures.
  }
};
